import { useState, useEffect } from "react";
import "../../../Styles/style.css";
import AdminApiModule from "../../../components/AdminApi/Api/api";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import ViewUser from "./ViewUser";
import { useSelector } from "react-redux";
import { FormControlLabel, FormGroup, Switch } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  canViewUsers,
  canAddUser,
  canUpdateUser,
  canActivateDeactivateUser,
  hasFullUserAccess,
  checkPermission,
} from "../../../utility/permissionUtils";

const AdminUsers = (props) => {
  const [usersList, setUsersList] = useState([]);
  const [isUserEdit, setIsUserEdit] = useState(false);
  const [viewfeedback, setViewFeedback] = useState([]);
  const [isViewUserOpen, setIsViewUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});
  const [toast, setToast] = useState({
    open: false,
    msg: "",
    duration: 5000,
    type: "",
  });

  const navigate = useNavigate();
  const reduxState = useSelector((state) => state.userReducer);
  const globalState = useSelector((state) => state.AdminReducer);

  // Get user permissions
  const userPermissions = reduxState?.adminDetails?.role_id?.permissions;

  // Check permissions with fallback
  const hasViewPermission = canViewUsers(userPermissions);
  const hasAddPermission = canAddUser(userPermissions);
  const hasUpdatePermission = canUpdateUser(userPermissions);
  const hasActivateDeactivatePermission =
    canActivateDeactivateUser(userPermissions);
  const hasFullAccess = hasFullUserAccess(userPermissions);

  const parseUserObject = (userObject) => {
    return {
      userName: userObject?.user_name,
      password: userObject?.password,
      phoneNumber: userObject?.phone?.mobile_number,
      roleName: userObject?.role_id.role_name,
    };
  };

  const handleViewUser = (row) => {
    if (!hasViewPermission && !hasFullAccess) {
      showDialog(true, "error", "You don't have permission to view users");
      return;
    }

    const userObjectValue = parseUserObject(row);
    setSelectedUser(userObjectValue);
    setIsViewUserOpen(true);
  };

  const handleCloseViewUser = () => {
    setIsViewUserOpen(false);
  };

  useEffect(() => {
    if (hasViewPermission || hasFullAccess) {
      getAllUsers();
    }
  }, [hasViewPermission, hasFullAccess]);

  const showDialog = (open, type, msg) => {
    setToast({
      open,
      msg,
      duration: 5000,
      type,
    });
  };

  const closeDialog = (open = false) => {
    setToast({
      open,
      msg: "",
      duration: 5000,
      type: "",
    });
  };

  const getAllUsers = () => {
    new AdminApiModule()
      .getAllUsers()
      .then((res) => {
        let parsedData = [];
        if (res.status == 401) {
          localStorage.removeItem("token");
          navigate("/login");
        } else if (
          res &&
          res.data &&
          res.data.allUsers &&
          res.data.allUsers.length > 0
        ) {
          parsedData = res.data.allUsers;
        }
        setUsersList(parsedData);
      })
      .catch((err) => {
        console.log(err);
        showDialog(true, "error", "Failed to fetch users");
      });
  };

  const handleAddUser = () => {
    if (!hasAddPermission && !hasFullAccess) {
      showDialog(true, "error", "You don't have permission to add users");
      return;
    }
    navigate("/add-user");
  };

  const handleDeleteUser = (id) => {
    if (!hasUpdatePermission && !hasFullAccess) {
      showDialog(true, "error", "You don't have permission to delete users");
      return;
    }

    const payload = {
      user_object_id: id,
    };
    new AdminApiModule()
      .adminRemoveUser(payload)
      .then((res) => {
        if (res) {
          showDialog(true, "success", "User deleted successfully");
        }
        getAllUsers();
      })
      .catch((err) => {
        console.log(err);
        showDialog(true, "error", err?.response?.data?.message);
      });
  };

  const handleChangeCheckBox = (e, id) => {
    if (!hasActivateDeactivatePermission && !hasFullAccess) {
      showDialog(
        true,
        "error",
        "You don't have permission to activate/deactivate users"
      );
      return;
    }

    const isActive = e.target.checked ? 1 : 0;
    const payload = {
      admin_user_id: id,
      is_active: isActive,
    };

    new AdminApiModule()
      .updateuserStatus(payload)
      .then((res) => {
        if (res) {
          const updatedUsersList = usersList.map((user) => {
            if (user._id === id) {
              return { ...user, is_active: isActive };
            }
            return user;
          });
          setUsersList(updatedUsersList);
          showDialog(
            true,
            "success",
            `User ${isActive ? "activated" : "deactivated"} successfully`
          );
        }
      })
      .catch((err) => {
        console.log(err);
        showDialog(true, "error", "Failed to update user status");
      });
  };

  // If user doesn't have view permission, show access denied message
  if (!hasViewPermission && !hasFullAccess) {
    return (
      <div
        className="access-denied"
        style={{
          textAlign: "center",
          padding: "50px",
          color: "#721c24",
          backgroundColor: "#f8d7da",
          border: "1px solid #f5c6cb",
          borderRadius: "5px",
          margin: "20px",
        }}
      >
        <h3>Access Denied</h3>
        <p>You don't have permission to view users.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Add User Button - Only show if user has add permission */}
      {(hasAddPermission || hasFullAccess) && (
        <div className="add-service-button">
          <Button
            variant="contained"
            className="text-transform-none"
            onClick={handleAddUser}
          >
            Add a User
          </Button>
        </div>
      )}

      {/* Users Table */}
      <div>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>User Name</TableCell>
                <TableCell>Mobile Number</TableCell>
                <TableCell>Role Given</TableCell>
                {/* User Status Column - Only show if user has activate/deactivate permission */}
                {(hasActivateDeactivatePermission || hasFullAccess) && (
                  <TableCell>User Status</TableCell>
                )}
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usersList && usersList.length > 0 ? (
                usersList.map((row) => (
                  <TableRow
                    key={row._id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {row.user_name}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {row.phone.country_code + row.phone.mobile_number}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {row?.role_id?.role_name}
                    </TableCell>
                    {/* User Status Switch - Only show if user has activate/deactivate permission */}
                    {(hasActivateDeactivatePermission || hasFullAccess) && (
                      <TableCell component="th" scope="row">
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={row.is_active}
                                onChange={(e) =>
                                  handleChangeCheckBox(e, row._id)
                                }
                                disabled={
                                  !hasActivateDeactivatePermission &&
                                  !hasFullAccess
                                }
                              />
                            }
                            label={row.is_active ? "ACTIVE" : "INACTIVE"}
                          />
                        </FormGroup>
                      </TableCell>
                    )}
                    <TableCell component="th" scope="row">
                      <Button
                        variant="outlined"
                        onClick={() => handleViewUser(row)}
                        disabled={!hasViewPermission && !hasFullAccess}
                      >
                        View User
                      </Button>
                      {/* Delete Button - Only show if user has update permission */}
                      {/* {(hasUpdatePermission || hasFullAccess) && (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleDeleteUser(row._id)}
                          style={{ marginLeft: "8px" }}
                        >
                          Delete
                        </Button>
                      )} */}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {/* View User Dialog */}
      <div>
        {isViewUserOpen && (
          <ViewUser
            isViewRoleOpen={isViewUserOpen}
            handleCloseViewRole={handleCloseViewUser}
            roleObject={selectedUser}
            hasUpdatePermission={hasUpdatePermission || hasFullAccess}
            hasActivateDeactivatePermission={
              hasActivateDeactivatePermission || hasFullAccess
            }
          />
        )}
      </div>

      {/* Toast Notification */}
      {toast.open && (
        <div
          className={`toast toast-${toast.type}`}
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            padding: "15px",
            borderRadius: "5px",
            color: "white",
            zIndex: 1000,
          }}
        >
          {toast.msg}
          <button
            onClick={() => closeDialog()}
            style={{
              marginLeft: "10px",
              background: "none",
              border: "none",
              color: "white",
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
